import React, { useState } from "react";

import NoAssignee from "@common/assets/images/unassigned-icon.png";
import * as Styled from "@common/components/Dropdown/AssigneeDropdown/AssigneeDropdown.styles";
import * as StyledDropdown from "@common/components/Dropdown/Dropdown.styles";
import { InputElementType } from "@common/components/Input/Input";
import RoundedCanvas from "@common/components/RoundedCanvas/RoundedCanvas";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { getUserInitials } from "@common/models";
import { Member } from "@common/models/Member.model";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import identifiers from "@common/util/identifiers.json";
import { useAppContext } from "@main/context/App/App.context";
import { useAuthContext } from "@main/context/Auth/Auth.context";
import { getRandomColor } from "@main/pages/shared/helpers/getRandomColorHelper";

interface AssigneeDropdownProps {
  assigneeId: string | null;
  changeAssignee: (assigneeId: string | null) => void;
}

export const AssigneeDropdown = ({ assigneeId, changeAssignee }: AssigneeDropdownProps) => {
  const { wsTeamMembers } = useAppContext();
  const { userData } = useAuthContext();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const member: Member | undefined = wsTeamMembers?.find((member) => member.id === assigneeId);
  const iconColor = member ? getRandomColor(member.user.id) : undefined;

  const handleChangeAssignee = (assigneeId: string | null) => {
    if ((assigneeId && assigneeId !== member?.id) || Boolean(!assigneeId && member) || Boolean(assigneeId && !member))
      changeAssignee(assigneeId);
    setIsMenuOpen(false);
    setInputValue("");
  };

  const tooltipText = !member ? "Unassigned" : `Assigned to ${member?.user.name}`;

  const members: Member[] =
    wsTeamMembers
      ?.filter((member) => member.user.id !== userData?.id)
      .sort((a, b) => a.user.name.localeCompare(b.user.name)) ?? [];

  const filteredMembers: Member[] = members.filter((m) => m.user.name.toLowerCase().includes(inputValue.toLowerCase()));
  return (
    <Styled.AssigneeDropdown
      wide
      buttonElement={
        <Tooltip
          text={tooltipText}
          position="top"
        >
          <RoundedCanvas
            letter={member ? getUserInitials(member.user.name) : undefined}
            bgColor={iconColor && iconColor.background}
            txtColor={iconColor && iconColor.text}
            image={member ? (member?.user.picture ? member?.user.picture : undefined) : NoAssignee}
          />
        </Tooltip>
      }
      position="left"
      isOpened={isMenuOpen}
      setIsOpened={setIsMenuOpen}
      testId={identifiers["central.column.assignee.icon"]}
      menuPadding={false}
      onMenuHidden={() => setInputValue("")}
    >
      <Styled.DropdownAssigneeInput
        elementType={InputElementType.INPUT}
        placeholder="Search..."
        value={inputValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
      />

      {isMenuOpen ? (
        <StyledDropdown.DropdownScroll cx={Styled.dropdownScroll}>
          <Styled.DropdownItem
            $clicked={member?.user.id === userData?.id}
            onClick={() =>
              handleChangeAssignee(wsTeamMembers?.find((member) => member.user.id === userData?.id)?.id ?? null)
            }
            testId={identifiers["central.column.assignee.dropdown.you"]}
          >
            <Flex>
              <Styled.Checkmark>
                {member?.user.id === userData?.id && (
                  <Styled.CheckmarkIcon
                    width={16}
                    height={16}
                  />
                )}
              </Styled.Checkmark>
              <p>{"You"}</p>
            </Flex>
          </Styled.DropdownItem>
          <Styled.DropdownItem
            $clicked={!member}
            onClick={() => handleChangeAssignee(null)}
            testId={identifiers["central.column.assignee.dropdown.unassigned"]}
          >
            <Flex>
              <Styled.Checkmark>
                {!member && (
                  <Styled.CheckmarkIcon
                    width={16}
                    height={16}
                  />
                )}
              </Styled.Checkmark>
              <p>{"Unassigned"}</p>
            </Flex>
          </Styled.DropdownItem>
          {filteredMembers.map((m) => {
            return (
              <AssigneeOption
                key={m.id}
                member={m}
                isSelected={m?.id === member?.id}
                changeAssignee={() => handleChangeAssignee(m.id)}
              />
            );
          })}
        </StyledDropdown.DropdownScroll>
      ) : null}
    </Styled.AssigneeDropdown>
  );
};

export interface AssigneeOptionProps {
  member: Member;
  isSelected: boolean;
  changeAssignee: (assigneeId: string | null) => void;
}

export const AssigneeOption = ({ member, isSelected, changeAssignee }: AssigneeOptionProps) => {
  return (
    <Styled.DropdownItem
      $clicked={isSelected}
      onClick={() => changeAssignee(member.id)}
      testId={identifiers["central.column.assignee.dropdown.assigneeListing"]}
    >
      <Flex>
        <Styled.Checkmark>
          {isSelected && (
            <Styled.CheckmarkIcon
              width={16}
              height={16}
            />
          )}
        </Styled.Checkmark>
        <p>{member.user.name}</p>
      </Flex>
    </Styled.DropdownItem>
  );
};
